import axios from 'axios';
import config from '@/config';

const api = axios.create({
  baseURL: config.api,
  timeout: config.apiTimeout,
});

api.interceptors.request.use(
  (conf) => {
    const token = localStorage.getItem('token');
    if (token) {
      // eslint-disable-next-line
      conf.headers.Authorization = `bearer ${token}`;
    }
    return conf;
  },
  (error) => Promise.reject(error), // Catch
);

export default {
  api,
  login({ username, password }) {
    return api.post('/auth/admin/login', { username, password });
  },
  logout() {
    return api.post('/auth/logout').then(() => {
      return true;
    });
  },

  // Countries
  getCountries(payload) {
    return api.post('/countries/get-countries', payload);
  },
  getCountry(id) {
    return api.get(`/countries/${id}`);
  },
  createCountry(payload) {
    return api.post('/countries/create-country', payload);
  },
  updateCountry(payload) {
    return api.put(`/countries/${payload.id}`, payload);
  },
  deleteCountries(ids) {
    return api.delete('/countries/delete-countries', {
      data: ids,
    });
  },

  // Towns
  getTowns(payload = {}) {
    return api.post('/towns/get-towns', payload);
  },
  getTown(id) {
    return api.get(`/towns/${id}`);
  },
  createTown(payload) {
    return api.post('/towns/create-town', payload);
  },
  updateTown(payload) {
    return api.put(`/towns/${payload.id}`, payload);
  },
  deleteTowns(ids) {
    return api.delete('/towns/delete-towns', {
      data: ids,
    });
  },

  // Users
  getProfile() {
    return api.get('/users/profile');
  },
  updateProfile(payload) {
    return api.put('/users/update-profile', payload);
  },
  updateProfileImage(payload) {
    return api.put('/users/update-profile-image', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  updateShippingInfoProfile(payload) {
    return api.put('/users/update-shipping-info', payload);
  },
  addUser(payload) {
    return api.post('/users', payload);
  },
  addShippingInfo(payload) {
    return api.post('/users/create-shipping-info', payload);
  },
  updateShippingInfoAdmin(payload) {
    return api.put('/users/update-shipping-info-admin', payload);
  },
  getUsers(payload) {
    return api.post('/users/get-users', payload);
  },
  getUserById(id) {
    return api.get(`/users/${id}`);
  },
  getShippingInfoForUserById(id) {
    return api.get(`/users/shipping-info?userId=${id}`);
  },
  updateUser(payload) {
    return api.put(`/users/${payload.id}`, payload);
  },
  activateUser(id) {
    return api.put(`/users/activate/${id}`);
  },
  deactivateUser(id) {
    return api.put(`/users/deactivate/${id}`);
  },
  checkEmail(payload) {
    return api({
      url: `/users/check-email`,
      method: 'POST',
      data: payload
    })
  },
  checkUsername(payload) {
    return api({
      url: `/users/check-username`,
      method: 'POST',
      data: payload
    })
  },
  verifyEmail(token, userId) {
    return api.get(`/users/verify-email?userId=${parseInt(userId, 10)}&token=${token}`);
  },
  deleteUsers(ids) {
    return api.delete('/users/delete-users', {
      data: ids
    });
  },
  register(payload) {
    return api.post('/users/register', payload);
  },
  getPersonalDataChangeRequests(payload) {
    return api({
      url: '/users/get-personal-data-change-request',
      data: payload,
      method: 'POST',
    });
  },
  getPersonalDataChangeRequest(id) {
    return api({
      url: `/users/get-personal-data-change-request/${id}`,
      method: 'GET',
    });
  },
  approvePersonalRequest(id) {
    return api({
      url: `/users/approve-personal-data-change-request/${id}`,
      method: 'POST',
    });
  },
  declinePersonalRequest(id) {
    return api({
      url: `/users/decline-personal-data-change-request/${id}`,
      method: 'POST',
    });
  },
  deletePersonalRequests(ids) {
    return api({
      url: '/users/delete-personal-data-requests',
      method: 'DELETE',
      data: ids,
    });
  },
  getB2BCustomerRequests(payload) {
    return api.post('/users/get-b2b-customer-request', payload);
  },
  approveB2BCustomerRequest(id) {
    return api({
      url: `/users/approve-b2b-customer-request?customerB2BRegistrationRequestId=${id}`,
      method: 'POST',
    });
  },
  declineB2BCustomerRequest(id) {
    return api({
      url: `/users/decline-b2b-customer-request?customerB2BRegistrationRequestId=${id}`,
      method: 'POST',
    });
  },
  deleteB2BCustomerRequests(ids) {
    return api.delete('/users/delete-b2b-requests', {
      data: ids
    });
  },
  resendVerificationMail(id) {
    return api({
      url: '/users/resend-verify-email',
      method: 'POST',
      params: {
        userId: id
      }
    });
  },

  // Companies
  getCompanies(payload = {
    sortBy: 'email',
    isSortAscending: true,
    pageSize: 10,
    retrieveAll: true,
    }) {
    return api.post('/companies/get-companies', payload);
  },
  getCompaniesV2(payload = {
    sortBy: 'email',
    isSortAscending: true,
    pageSize: 10,
    retrieveAll: true,
    }) {
    return api.post('/companies/get-companies-v2', payload);
  },
  getCompanySuperBonusPaged(payload) {
    return api.post('/companies/get-company-super-bonuses', payload);
  },
  updateSuperBonus(id, payload) {
    return api({
      url: `/companies/update-super-bonus/${id}`,
      data: payload,
      method: 'PUT',
    });
  },
  getSimplifiedCompanies(payload) {
    return api({
      url: '/companies/simplified',
      method: 'POST',
      data: payload,
    });
  },
  getMasterCompanies() {
    return api.get('/companies/get-master-companies');
  },
  createCompany(payload) {
    return api({
      url: '/companies/create-company',
      data: payload,
      method: 'POST'
    });
  },
  getCompanyById(id) {
    return api.get(`/companies/${id}`);
  },
  getCompanySuperBonusByCompanyId(id) {
    return api.get(`/companies/super-bonus/${id}`);
  },
  updateCompanySuperBonus(id, payload) {
    return api({
      url: `/companies/super-bonus/${id}`,
      data: payload,
      method: 'POST'
    });
  },
  createCompanySuperBonus(id, payload) {
    return api({
      url: `/companies/create-super-bonus/${id}`,
      data: payload,
      method: 'POST'
    });
  },
  deleteCompanies(ids) {
    return api.delete('/companies/delete-companies', {
      data: ids,
    });
  },
  deleteSuperBonus(id) {
    return api.delete(`/companies/deactivate-super-bonus/${id}`);
  },
  updateCompany(payload) {
    return api({
      url: `/companies/${payload.id}`,
      data: payload,
      method: 'PUT',
    });
  },

  createCompanyArticlePrice(article) {
    return api({
      url: '/company-articles-prices/create-company-article-price',
      method: 'POST',
      data: article,
    });
  },
  updateCompanyArticlePrice(article) {
    return api({
      url: `/company-articles-prices/${article.id}`,
      method: 'PUT',
      data: article,
    });
  },
  deleteCompanyArticle(id) {
    return api({
      url: `/company-articles-prices/${id}`,
      method: 'DELETE',
    });
  },
  getCompanyArticles(companyId) {
    return api({
      url: `/company-articles-prices/${companyId}`,
      method: 'GET',
    });
  },

  // Commercialists
  getCommercialists() {
    return api.get('/companies/get-commercialists');
  },
  getCommercialistsForCompany(companyId) {
    return api.get(`/companies/get-commercialists/company/${companyId}`);
  },

  // Categories
  getCategories(payload) {
    return api.post('/categories/get-categories', payload);
  },
  getParentCategories(payload) {
    return api.post('/categories/get-parent-categories', payload);
  },
  getChildCategories(payload) {
    return api.post('/categories/get-child-categories', payload);
  },
  getSimplifiedCategories(payload) {
    return api.post('/categories/simplified', payload);
  },
  getCategoryById(categoryId) {
    return api.post('/categories/get-categories', { categoryId });
  },
  addCategory(payload) {
    return api.post('/categories/create-category', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  updateCategory(payload) {
    return api.put('/categories/' + payload.get('id'), payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  deleteCategories(ids) {
    return api.delete('/categories/delete-categories', {
      data: ids
    });
  },
  getCategoryArticles(payload) {
    return api({
      url: '/categories/get-category-articles',
      data: payload,
      method: 'POST'
    });
  },
  saveArticlesPriority(payload) {
    return api({
      url: '/categories/save-articles-priority',
      data: payload,
      method: 'POST',
    });
  },
  deleteArticlePriority(id) {
    return api({
      url: `/categories/delete-article-priority/${id}`,
      method: 'DELETE',
    });
  },
  getPriorityArticles(payload) {
    return api({
      url: `/categories/get-priority-articles?categoryId=${payload.categoryId}`,
      data: payload,
      method: 'POST',
    });
  },

  // Articles
  getStockAmountOfArticles(payload) {
    return api.post('/articles/get-stock-amount-of-articles', payload);
  },
  getArticles(payload) {
    return api.post('/articles/get-articles', payload);
  },
  getArticleById(articleId) {
    return api.get(`/articles/${articleId}`);
  },
  checkIfArticleCodeExists(articleId, code) {
    return api.get(`/articles/check-if-code-exists/${articleId}/${code}`);
  },
  createArticle(payload) {
    return api({
      url: '/articles/create-article',
      method: 'POST',
      data: payload,
    });
  },
  updateArticle(payload) {
    return api({
      url: `/articles/${payload.id}`,
      method: 'PUT',
      data: payload,
    });
  },
  importArticles(payload) {
    return api({
      timeout: 60 * 1000 * 20,
      url: '/articles/import-articles',
      data: payload,
      method: 'POST',
    });
  },
  deleteArticles(ids) {
    return api.delete('/articles/delete-articles', {
      data: ids
    });
  },
  deleteArticleImages(ids) {
    return api.delete('/articles/delete-images', {
      data: ids
    });
  },
  setImageOrdinals(payload) {
    return api({
      url: '/articles/set-images-ordinals',
      method: 'POST',
      data: payload,
    });
  },
  createLinkedArticles(payload) {
    return api.post('/articles/create-linked-article', payload);
  },
  updateLinkedArticles(payload) {
    return api.put('/articles/update-linked-articles', payload);
  },
  createArticleAttributes(payload) {
    return api.post('/articles/create-article-attribute', payload);
  },
  updateArticleAttributes(payload) {
    return api.put('/articles/update-article-attribute', payload);
  },
  deleteArticleAttributeValueId({ articleId, attributeId, attributeValueId }) {
    return api.delete('/articles/delete-article-attribute', {
      data: { articleId, attributeId, attributeValueId }
    });
  },
  deleteArticleAttribute({ articleId, attributeId }) {
    return api.delete('/articles/delete-article-attributes', {
      data: { articleId, attributeId }
    });
  },
  getPresentationRequests(payload) {
    return api.post('/articles/get-articles-presentations', payload);
  },
  markPresentationAsHeld(id) {
    return api({
      url: `/articles/mark-presentation-held?articlePresentationRequestId=${id}`,
      method: 'PUT',
    });
  },
  getArticleFilters() {
    return api({
      url: '/articles/get-articles/backoffice/filters',
      method: 'GET',
    });
  },
  deleteArticlePresentations(ids) {
    return api.delete('/articles/delete-article-presentations', {
      data: ids
    });
  },

  // Banks
  getBanks(payload) {
    return api.post('/banks/get-banks', payload);
  },
  getBank(id) {
    return api.get(`/banks/${id}`);
  },
  createBank(payload) {
    return api.post('/banks/create-bank', payload);
  },
  updateBank(payload) {
    return api.put(`/banks/${payload.id}`, payload);
  },
  deleteBanks(ids) {
    return api.delete('/banks/delete-banks', {
      data: ids
    });
  },

  // Currencies
  getCurrencies(payload) {
    return api.post('/currencies/get-currencies', payload);
  },
  createCurrency(payload) {
    return api.post('/currencies/create-currency', payload);
  },
  getCurrency(id) {
    return api.get(`/currencies/${id}`);
  },
  updateCurrency(payload) {
    return api.put(`/currencies/${payload.id}`, payload);
  },
  deleteCurrencies(ids) {
    return api.delete('/currencies/delete-currencies', {
      data: ids
    });
  },

  // Units
  getMeasurementUnits(payload) {
    return api.post('/unit-of-measures/get-unit-of-measures', payload);
  },
  getMeasurementUnit(id) {
    return api.get(`/unit-of-measures/${id}`);
  },
  createMeasurementUnit(payload) {
    return api.post('/unit-of-measures/create-unit-of-measure', payload);
  },
  updateMeasurementUnit(payload) {
    return api.put(`/unit-of-measures/update-unit-of-measure/${payload.id}`, payload);
  },
  deleteMeasurementUnit(payload) {
    return api({
      url: '/unit-of-measures/delete-uom',
      method: 'DELETE',
      data: payload,
    });
  },

  // Brands
  getBrands(payload) {
    return api.post('/brands/get-brands', payload);
  },
  getBrand(id) {
    return api.get(`/brands/${id}`);
  },
  createBrand(payload) {
    return api.post('/brands/create-brand', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  updateBrand(payload) {
    return api.put('/brands/' + payload.get('id'), payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  deleteBrands(ids) {
    return api.delete('/brands/delete-brands', {
      data: ids
    });
  },

  // Taxes
  getTaxes(payload) {
    return api.post('/taxes/get-taxes', payload);
  },

  // Express posts
  getExpressPosts(payload) {
    return api.post('/express-posts/get-express-posts', payload);
  },
  createExpressPost(payload) {
    return api.post('/express-posts/create-express-post', payload);
  },
  getExpressPost(id) {
    return api.get(`/express-posts/${id}`);
  },
  updateExpressPost(payload) {
    return api.put(`/express-posts/${payload.id}`, payload);
  },
  deleteExpressPosts(ids) {
    return api.delete('/express-posts/delete-express-posts', {
      data: ids
    });
  },

  // Tabs
  getTabs(payload) {
    return api.post('/tabs/get-tabs', payload);
  },
  getTab(id) {
    return api.get(`/tabs/${id}`);
  },
  createTab(payload) {
    return api.post('/tabs/create-tab', payload);
  },
  updateTab(payload) {
    return api.put(`/tabs/${payload.id}`, payload);
  },
  deleteTabs(ids) {
    return api.delete('/tabs/delete-tabs', {
      data: ids
    });
  },

  // Product sales
  getProductSales(payload) {
    return api.post('/product-sales/get-product-sales', payload);
  },
  getProductSale(id) {
    return api.get(`/product-sales/${id}`);
  },
  createProductSale(payload) {
    return api.post('/product-sales/create-product-sale', payload);
  },
  updateProductSale(payload) {
    return api.put(`/product-sales/${payload.id}`, payload);
  },
  deleteProductSales(ids) {
    return api.delete('/product-sales/delete-product-sales', {
      data: ids
    });
  },

  // Interests area
  getInterestsAreas(payload) {
    return api.post('/interests-areas/get-interests-areas', payload);
  },
  getInterestsArea(id) {
    return api.get(`/interests-areas/${id}`);
  },
  createInterestsArea(payload) {
    return api.post('/interests-areas/create-interests-area', payload);
  },
  updateInterestsArea(payload) {
    return api.put(`/interests-areas/${payload.id}`, payload);
  },
  deleteInterestsAreas(ids) {
    return api.delete('/interests-areas/delete-interests-areas', {
      data: ids
    });
  },

  // Customer accounts
  getCustomerBankAccounts(payload) {
    return api.post('/customer-bank-accounts/get-customer-bank-accounts', payload);
  },
  getCustomerBankAccount(id) {
    return api.get(`/customer-bank-accounts/${id}`);
  },
  createCustomerBankAccount(payload) {
    return api.post('/customer-bank-accounts/create-customer-bank-account', payload);
  },
  updateCustomerBankAccount(payload) {
    return api.put(`/customer-bank-accounts/${payload.id}`, payload);
  },
  deleteCustomerBankAccounts(ids) {
    return api.delete('/customer-bank-accounts/delete-customer-bank-accounts', {
      data: ids
    });
  },

  // Blogs
  getBlogs(payload) {
    return api.post('/blogs/get-blogs', payload);
  },
  getBlog(id) {
    return api.get(`/blogs/${id}`);
  },
  createBlog(payload) {
    return api.post('/blogs/create-blog', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  updateBlog(payload) {
    return api.put('/blogs/' + payload.get('id'), payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  deleteBlogs(ids) {
    return api.delete('/blogs/delete-blogs', {
      data: ids
    });
  },

  // Terms of service
  getTermsOfServices() {
    return api.post('/terms-of-services/get-terms-of-services');
  },
  updateTermsOfServices(payload) {
    return api.put(`/terms-of-services/${payload.id}`, payload);
  },

  // Attributes
  getAttributes(payload) {
    return api.post('/attributes/get-attributes', payload);
  },
  getAttribute(id) {
    return api.get(`/attributes/${id}`);
  },
  createAttribute(payload) {
    return api.post('/attributes/create-attribute', payload);
  },
  updateAttribute(payload) {
    return api.put(`/attributes/update-attribute/${payload.id}`, payload);
  },
  deleteAttributes(ids) {
    return api.delete('/attributes/delete-attributes', {
      data: ids
    });
  },
  deleteAttribute(id) {
    return api.delete(`/attributes/delete-attribute/${id}`);
  },
  deleteAttributeValue(id) {
    return api.delete(`/attributes/delete-attribute-value/${id}`);
  },
  getAttributesByIds(payload) {
    return api.post('/attributes/get-attributes-by-ids', payload)
  },
  

  // Variations
  createVariation(payload) {
    return api.post('/variations/create-variation', payload);
  },
  createVariations(payload) {
    return api.post('/variations/create-variations', payload);
  },
  updateVariation(payload) {
    return api.put(`/variations/update-variation/${payload.id}`, payload);
  },
  getArticleVariations(id) {
    return api.get(`/variations/${id}`);
  },
  getVariationsWarehouseTownsByArticleId(id) {
    return api.get(`/variations/${id}/warehouse-towns`);
  },
  deleteVariationImage(payload) {
    return api.delete('/variations/delete-variation-image', {
      data: payload
    });
  },
  deleteVariation(variationId) {
    return api.delete(`/variations/${variationId}`);
  },

  // System messages
  getSystemMessages(payload) {
    return api.post('/system-messages/get-system-messages', payload);
  },
  getSystemMessagesAsAdmin(payload) {
    return api.post('/system-messages/get-system-messages-as-admin', payload);
  },
  createSystemMessage(payload) {
    return api.post('/system-messages/create-system-message', payload);
  },
  deleteSystemMessages(ids) {
    return api.delete('/system-messages/delete-system-messages', {
      data: ids
    });
  },

  // Highlighted brands
  getHighlightedBrands(payload) {
    return api.post('/main-page/get-highlighted-brands', payload);
  },
  getHighlightedBrand(id) {
    return api({
      url: `/main-page/highlighted-brands/${id}`
    });
  },
  createHighlightedBrand(data) {
    return api({
      url: '/main-page/create-highlighted-brand',
      method: 'POST',
      data: data,
    });
  },
  updateHighlightedBrand(data) {
    return api({
      url: `/main-page/highlighted-brands/${data.id}`,
      method: 'PUT',
      data,
    });
  },
  deleteHighlightedBrand(id) {
    return api({
      url: `/main-page/delete-highlighted-brand?highlightedBrandId=${id}`,
      method: 'DELETE'
    });
  },
  deleteHighlightedBrands(ids) {
    return api.delete('/main-page/delete-highlighted-brands', {
      data: ids
    });
  },

  // Jumbotrons
  getJumbotrons(payload) {
    return api.post('/main-page/get-jumbotrons', payload);
  },
  getJumbotronById(id) {
    return api({
      url: `/main-page/jumbotrons/${id}`,
      method: 'GET'
    });
  },
  updateJumbotron(payload) {
    return api({
      url: `/main-page/jumbotrons/${payload.get('id')}`,
      method: 'PUT',
      data: payload,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      },
    });
  },
  createJumbotron(payload) {
    return api({
      url: `/main-page/create-jumbotron`,
      method: 'POST',
      data: payload,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      },
    });
  },
  deleteJumbotron(id) {
    return api.delete(`/main-page/delete-jumbotron?jumbotronId=${id}`);
  },
  deleteJumbotrons(ids) {
    return api.delete('/main-page/delete-jumbotrons', {
      data: ids
    });
  },

  // Highlighted categories
  getHighlightedCategories(payload) {
    return api.post('/main-page/get-highlighted-categories', payload);
  },
  createHighlightedCategory(data) {
    return api({
      url: `/main-page/create-highlighted-category`,
      method: 'POST',
      data,
    });
  },
  updateHighlightedCategory(data) {
    return api({
      url: `/main-page/highlighted-category/${data.id}`,
      method: 'PUT',
      data,
    });
  },
  getHighlightedCategory(id) {
    return api({
      url: `/main-page/highlighted-category/${id}`,
      method: 'GET',
    });
  },
  deleteHighlightedCategory(id) {
    return api({
      url: `/main-page/delete-highlighted-category?highlightedCategoryId=${id}`,
      method: 'DELETE',
    });
  },
  deleteHighlightedCategories(ids) {
    return api.delete('/main-page/delete-highlighted-categories', {
      data: ids
    });
  },

  // Banneri (editorial na početnoj — BannerType 3 = RightSidePage)
  getBanners(payload) {
    return api.get('/main-page/get-banners', { params: payload });
  },
  getBanner(id) {
    return api({
      url: `/main-page/highlighted-banners/${id}`,
      method: 'GET',
    });
  },
  createBanner(data) {
    return api({
      url: `/main-page/create-banner`,
      method: 'POST',
      data,
    });
  },
  updateBanner(id, data) {
    return api({
      url: `/main-page/highlighted-banners/${id}`,
      method: 'PUT',
      data,
    });
  },
  deleteBanner(id) {
    return api({
      url: `/main-page/delete-banner?bannerId=${id}`,
      method: 'DELETE',
    });
  },

  // Newly added articles
  getNewlyAddedArticles(payload) {
    return api.post('/articles/marked-as-new-articles', payload);
  },
  removeMarkedAsNewAricles(ids) {
    return api.delete('/articles/delete-marked-as-new-articles', {
      data: ids
    });
  },

  // Tags
  getArticleTags() {
    return api.get('/articles/article-tags');
  },
  getTags(payload) {
    return api.post('/tags/get-tags', payload);
  },
  getTag(id) {
    return api.get(`/tags/${id}`);
  },
  createTag(payload) {
    return api.post('/tags/create-tag', payload);
  },
  updateTag(payload) {
    return api.put(`/tags/${payload.id}`, {
      title: payload.title,
    });
  },
  deleteTags(ids) {
    return api.delete('/tags/delete-tags', {
      data: ids
    });
  },

  // Shopping carts
  getShoppingCarts(payload) {
    return api({
      url: '/shopping-cart/get-shopping-carts',
      method: 'POST',
      data: payload
    });
  },
  getShoppingCartArticles(id) {
    return api({
      url: `/shopping-cart/${id}`,
      method: 'GET',
    });
  },
  sendEmailReminderForShoppingCart(shoppingCartId, customMessage) {
    return api({
      url: '/shopping-cart/email-reminder',
      method: 'POST',
      data: { shoppingCartId, customMessage }
    });
  },

  // Wish lists
  getWishLists(payload) {
    return api({
      url: '/wish-list/get-wish-lists',
      method: 'POST',
      data: payload
    })
  },
  getWishListItems(userId) {
    return api({
      url: `/wish-list/items-by-user/${userId}`,
      method: 'GET',
    })
  },

  // Orders
  getOrders(payload) {
    return api({
      url: '/orders/get-orders',
      method: 'POST',
      data: payload
    });
  },
  mergeOrders(payload) {
    return api({
      url: '/orders/merge-orders',
      method: 'POST',
      data: payload
    });
  },
  createOrdersBySplittingOrder(payload) {
    return api({
      url: '/orders/create-orders-by-splitting-order',
      method: 'POST',
      data: payload
    });
  },
  deleteOrders(payload) {
    return api({
      url: '/orders/delete-orders',
      method: 'DELETE',
      data: payload
    });
  },
  getOrder(id) {
    return api({
      url: `/orders/${id}`,
      method: 'GET',
    });
  },
  updateOrderStatus(payload) {
    return api({
      url: '/orders/update-order-status',
      method: 'PUT',
      data: payload,
    });
  },
  updateOrderDescription(orderId, orderDescription) {
    return api({
      url: '/orders/update-order-description',
      method: 'PUT',
      data: {
        orderId,
        orderDescription
      }
    });
  },
  updateOrderCompany(payload) {
    return api({
      url: '/orders/update-order-company',
      method: 'PUT',
      data: payload
    });
  },
  getOrderStates(payload) {
    return api({
      url: '/orders/get-order-states',
      method: 'GET',
      params: payload,
    });
  },
  addOrderState(payload) {
    return api({
      url: '/orders/create-order-state',
      method: 'POST',
      data: payload,
    });
  },
  deleteOrderState(id) {
    return api({
      url: `/orders/delete-order-state?orderStateId=${id}`,
      method: 'DELETE',
    });
  },
  exportOrder(id) {
    return api({
      url: `/orders/export-csv?id=${id}`,
      method: 'POST',
    })
  },
  bulkExportOrder(payload) {
    return api({
      url: `/orders/bulk-export-csv`,
      method: 'POST',
      data: payload,
    })
  },
  updateDeliveryType(payload) {
    return api({
      url: '/orders/update-order-delivery-type',
      method: 'PUT',
      data: payload,
    });
  },
  sendOrderToPostExpress(orderId) {
    return api({
      url: `/orders/send-order-to-express-post?orderId=${orderId}`,
      method: 'POST',
    });
  },
  deleteOrderItem(orderItemId) {
    return api({
      url: `/orders/delete-order-item?orderItemId=${orderItemId}`,
      method: 'DELETE',
    });
  },
  addOrderItem(payload) {
    return api({
      url: '/orders/add-order-item',
      method: 'POST',
      data: payload
    });
  },
  deleteOrderItems(payload) {
    return api.delete('/orders/delete-order-items', {
      data: payload
    });
  },
  updateOrderItemQuantity(id, quantity) {
    return api({
      url: `/orders/update-order-item-quantity?orderItemId=${id}&quantity=${quantity}`,
      method: 'PUT',
    });
  },
  updateOrderItemsQuantity(payload) {
    return api({
      url: '/orders/update-order-items-quantity',
      method: 'PUT',
      data: payload
    });
  },

  // Newsletters
  getNewsletters(payload) {
    return api({
      url: '/newsletter/get-newsletters',
      method: 'POST',
      data: payload,
    });
  },
  deleteNewsletters(ids) {
    return api.delete('/newsletter/delete-newsletters', {
      data: ids,
    });
  },
  exportNewsletters() {
    return api({
      url: `/newsletter/excel-export`,
      method: 'GET',
      responseType: 'blob',
    })
  },

  // Roles
  getRoles() {
    return api({
      url: '/roles/get-roles-simplified',
      method: 'GET',
    });
  },
  getRolesSimplified() {
    return api({
      url: '/roles/get-roles-simplified',
      method: 'GET'
    });
  },
  getRole(roleId) {
    return api({
      url: `/roles/${roleId}`,
      method: 'GET',
    });
  },
  createRole(payload) {
    return api({
      url: '/roles',
      method: 'POST',
      data: payload,
    });
  },
  updateRole(payload) {
    return api({
      url: `/roles/${payload.id}`,
      method: 'PUT',
      data: payload,
    });
  },
  deleteRole(payload) {
    return api({
      url: '/roles',
      method: 'DELETE',
      data: payload,
    });
  },
  deleteRoles(ids) {
    return api.delete('/roles', {
      data: ids,
    });
  },

  // Permissions
  getPermissions() {
    return api({
      url: '/roles/get-permissions',
      method: 'GET',
    });
  },

  // Service managers
  getServiceManagers(payload) {
    return api({
      url: '/service-managers/get-service-managers',
      method: 'POST',
      data: payload,
    });
  },
  getServiceManager(id) {
    return api({
      url: `/service-managers/${id}`,
      method: 'GET',
    });
  },
  updateServiceManager(payload) {
    return api({
      url: `/service-managers/update-service-manager/${payload.id}`,
      method: 'PUT',
      data: payload,
    });
  },
  createServiceManager(payload) {
    return api({
      url: '/service-managers/create-service-manager',
      method: 'POST',
      data: payload,
    });
  },

  // Customer files
  getCustomerFiles(payload) {
    return api({
      url: '/user-files/get-company-files-as-admin',
      method: 'POST',
      data: payload,
    });
  },
  deleteCustomerFiles(ids) {
    return api.delete('/user-files/delete-users-files', {
      data: ids,
    });
  },
  createCustomerFile(payload) {
    return api({
      url: '/user-files/create-company-file',
      method: 'POST',
      data: payload,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  },
  getCustomerFileAsAdmin(id) {
    return api({
      url: `/user-files/get-user-file-as-admin/${id}`,
      method: 'GET',
    });
  },
  updateCustomerFile(id, payload) {
    return api({
      url: `/user-files/${id}`,
      method: 'PUT',
      data: payload,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  },

  // RMA
  getComplaints(payload) {
    return api({
      url: '/complaints/get-complaints',
      method: 'POST',
      data: payload,
    });
  },
  getComplaint(id) {
    return api({
      url: `/complaints/${id}`,
      method: 'GET',
    });
  },
  updateComplaint(payload) {
    return api({
      url: `/complaints/${payload.id}`,
      method: 'PUT',
      data: payload,
    });
  },
  deleteComplaints(ids) {
    return api.delete('/complaints/delete-complaints', {
      data: ids,
    });
  },

  // Media library
  getMediaLibrary(payload) {
    return api({
      url: '/media-library/get-media-library',
      method: 'POST',
      data: payload,
    });
  },
  checkMediaLibraryUsage(payload) {
    return api({
      url: '/media-library/check-media-library-usage',
      data: payload,
      method: 'POST',
    });
  },
  deleteMediaLibraryFiles(payload) {
    return api({
      url: '/media-library/delete-media-library-files',
      data: payload,
      method: 'DELETE',
    });
  },

  // Meta SEO
  getMetaSeo(type) {
    return api({
      url: `/meta-seo/${type}`,
      method: 'GET',
    })
  },
  updateMetaSeo(type, payload) {
    return api({
      url: `/meta-seo/${type}`,
      method: 'PUT',
      data: payload,
    });
  },

  // Reviews
  getReviews(payload) {
    return api({
      url: '/article-reviews/get-reviews',
      data: payload,
      method: 'POST',
    });
  },
  getReview(id) {
    return api({
      url: `/article-reviews/${id}`,
      method: 'GET',
    });
  },
  updateReview(payload) {
    return api({
      url: '/article-reviews/update-review',
      data: payload,
      method: 'PUT',
    });
  },

  // Promo codes
  createPromoCode(payload) {
    return api({
      url: '/discount-codes/create-promo-code',
      data: payload,
      method: 'POST',
    });
  },
  updatePromoCode(payload) {
    return api({
      url: `/discount-codes/update-promo-code/${payload.id}`,
      data: payload,
      method: 'PUT',
    });
  },
  getPromoCodes(payload) {
    return api({
      url: '/discount-codes/get-promo-codes',
      data: payload,
      method: 'POST',
    });
  },
  getPromoCode(id) {
    return api({
      url: `/discount-codes/get-promo-code/${id}`,
      method: 'GET',
    });
  },

  // Gift cards
  createGiftCard(payload) {
    return api({
      url: '/discount-codes/create-gift-card',
      data: payload,
      method: 'POST',
    });
  },
  getGiftCards(payload) {
    return api({
      url: '/discount-codes/get-gift-cards',
      data: payload,
      method: 'POST',
    });
  },
  getGiftCard(id) {
    return api({
      url: `/discount-codes/get-gift-card/${id}`,
      method: 'POST',
    });
  },
  updateGiftCard(payload) {
    return api({
      url: `/discount-codes/update-gift-card/${payload.id}`,
      data: payload,
      method: 'PUT',
    });
  },

  // Top brands
  getTopBrandList(payload) {
    return api({
      url: '/main-page/get-top-brand-list',
      data: payload,
      method: 'POST',
    });
  },
  getTopBrandListById(id) {
    return api({
      url: `/main-page/top-brand?topBrandId=${id}`,
      method: 'GET',
    });
  },
  createTopBrandList(payload) {
    return api({
      url: '/main-page/create-top-brand-list',
      data: payload,
      method: 'POST',
    });
  },
  updateTopBrandList(payload) {
    return api({
      url: '/main-page/update-top-brand-list',
      data: payload,
      method: 'PUT',
    });
  },
  deleteTopBrandList(id) {
    return api({
      url: `/main-page/delete-top-brands-list?topBrandsListId=${id}`,
      method: 'DELETE',
    });
  },

  // Offers
  createOffer(payload) {
    return api({
      url: '/documents/create-document',
      data: payload,
      method: 'POST',
    });
  },
  createAdvancedOffer(payload) {
    return api({
      url: '/documents/create-advanced-document',
      data: payload,
      method: 'POST',
    });
  },
  getOffers(payload) {
    return api({
      url: '/documents/get-documents',
      data: payload,
      method: 'POST',
    });
  },
  getAdvancedOffers(payload) {
    return api({
      url: '/documents/get-advanced-documents',
      data: payload,
      method: 'POST',
    });
  },
  getOffer(id) {
    return api({
      url: `/documents/${id}`,
      method: 'GET',
    });
  },
  getAdvancedOffer(id) {
    return api({
      url: `/documents/advanced-document/${id}`,
      method: 'GET',
    });
  },
  updateOffer(payload) {
    return api({
      url: `/documents/${payload.id}`,
      data: payload,
      method: 'PUT',
    });
  },
  updateAdvancedOffer(payload) {
    return api({
      url: `/documents/advanced/${payload.id}`,
      data: payload,
      method: 'PUT',
    });
  },
  updateOfferItems(id, payload) {
    return api({
      url: `/documents/update-document-items/${id}`,
      data: payload,
      method: 'PUT',
    });
  },
  updateOfferItem(payload) {
    return api({
      url: `/documents/advanced/update-document-item`,
      data: payload,
      method: 'PUT',
    });
  },
  advancedUpdateOfferItems(id, payload) {
    return api({
      url: `/documents/advanced/update-document-items/${id}`,
      data: payload,
      method: 'PUT',
    });
  },
  deleteOffers(payload) {
    return api({
      url: '/documents/delete-documents',
      data: payload,
      method: 'DELETE',
    });
  },
  deleteOfferItems(id, payload) {
    return api({
      url: `/documents/delete-document-items/${id}`,
      data: payload,
      method: 'DELETE',
    });
  },
  getNextOrdinalNumber() {
    return api({
      url: '/documents/get-next-ordinal-number',
      method: 'GET',
    });
  },

  // Settings
  getSettings(key) {
    return api({
      url: `/settings/get-settings?key=${key}`,
      method: 'GET',
    });
  },
  updateSettings(key, payload) {
    return api({
      url: '/settings/update-settings',
      method: 'PUT',
      data: {
        key,
        data: JSON.stringify(payload)
      }
    });
  },
  getShowOffers() {
    return api({
      url: '/b2b-settings/show-offers',
    });
  },
  getShowAdvancedOffers() {
    return api({
      url: '/b2b-settings/show-advanced-offers',
    });
  },

  // B2C Settings
  getB2CSettings(key) {
    return api({
      url: `/settings/b2c-get-settings?key=${key}`,
      method: 'GET',
    });
  },
  updateB2CSettings(key, payload) {
    return api({
      url: '/settings/b2c-update-settings',
      method: 'PUT',
      data: {
        key,
        data: JSON.stringify(payload)
      }
    });
  },

  // Cleaning areas
  getCleaningAreas(payload) {
    return api({
      url: '/cleaning-plan/get-cleaning-areas',
      data: payload,
      method: 'POST',
    });
  },
  getCleaningAreaById(id) {
    return api({
      url: `/cleaning-plan/get-cleaning-area-by-id?cleaningAreaId=${id}`,
      method: 'GET',
    });
  },
  createCleaningArea(payload) {
    return api({
      url: '/cleaning-plan/create-cleaning-area',
      data: payload,
      method: 'POST',
    });
  },
  updateCleaningArea(payload) {
    return api({
      url: '/cleaning-plan/update-cleaning-area',
      data: payload,
      method: 'PUT',
    });
  },
  deleteCleaningArea(payload) {
    return api({
      headers: { 'Content-Type': 'application/json' },
      url: '/cleaning-plan/delete-cleaning-area',
      data: payload,
      method: 'DELETE',
    });
  },
  deleteCleaningAreas(payload) {
    return api({
      headers: { 'Content-Type': 'application/json' },
      url: '/cleaning-plan/delete-cleaning-area',
      data: payload,
      method: 'DELETE',
    });
  },
  deleteCleaningAreas2(ids) {
    return api.delete('/cleaning-plan/delete-cleaning-areas', {
      data: ids,
    });
  },

  // Cleaning plan note icons
  getCleaningPlanNoteIcons() {
    return api({
      url: '/cleaning-plan/get-note-icons',
      method: 'GET',
    });
  },
  getCleaningPlanNoteIcon(id) {
    return api({
      url: `/cleaning-plan/get-note-icon?cleaningPlanNoteIconId=${id}`,
      method: 'GET',
    });
  },
  createCleaningPlanNoteIcon(payload) {
    return api({
      url: '/cleaning-plan/create-note-icon',
      data: payload,
      method: 'POST',
    });
  },
  updateCleaningPlanNoteIcon(payload) {
    return api({
      url: '/cleaning-plan/update-note-icon',
      data: payload,
      method: 'PUT',
    });
  },

  // Cleaning plans
  createCleaningPlan(payload) {
    return api({
      url: '/cleaning-plan/create-cleaning-plan',
      data: payload,
      method: 'POST'
    });
  },
  getCleaningPlans(payload) {
    return api({
      url: '/cleaning-plan/get-cleaning-plans',
      data: payload,
      method: 'POST'
    });
  },
  getCleaningPlan(id) {
    return api({
      url: `/cleaning-plan/get-cleaning-plan-by-id?cleaningPlanId=${id}`,
      method: 'GET'
    });
  },
  updateCleaningPlan(payload) {
    return api({
      url: '/cleaning-plan/update-cleaning-plan',
      data: payload,
      method: 'PUT'
    });
  },
  deleteCleaningPlans(payload) {
    return api({
      url: '/cleaning-plan/delete-cleaning-plans',
      data: payload,
      method: 'DELETE'
    });
  },
  getCleaningPlanArticles(payload) {
    return api({
      url: '/articles/get-cleaning-plan-articles',
      data: payload,
      method: 'POST',
    });
  },

  // Price policies
  getPricePolicies(payload) {
    return api({
      url: '/price-policies/get-price-policies',
      data: payload,
      method: 'POST',
    });
  },
  getPricePolicy(payload) {
    return api({
      url: `/policy-prices/get-policy-price?companyId=${payload.companyId}`,
      data: payload,
      method: 'POST',
    });
  },
  getPricePolicyItems(payload) {
    return api({
      url: `/policy-prices/get-policy-price-items?companyId=${payload.companyId}`,
      data: payload,
      method: 'POST',
    });
  },
  deletePricePolicyItems(payload) {
    return api({
      url: '/policy-prices/delete-item',
      data: payload,
      method: 'DELETE',
    });
  },
  savePricePolicy(payload) {
    return api({
      url: '/policy-prices/save-policy-price',
      data: payload,
      method: 'POST',
    });
  },
  savePricePolicyItem(payload) {
    return api({
      url: '/policy-prices/save-policy-price-item',
      data: payload,
      method: 'POST',
    });
  },
  getDefaultPricePolicyTypes() {
    return api({
      url: '/policy-prices/get-default-types',
      method: 'GET',
    });
  },
  getActivyPolicyTypes(payload){
    return api({
      url: `/policy-prices/get-active-policy-types?pricePolicyId=${payload.pricePolicyId}`,
      method: 'GET',
    });
  },
  updatePricePolicyItemsPriorities(payload) {
    return api({
      url: '/policy-prices/update-policy-price-items-priorities',
      method: 'POST',
      data: payload,
    });
  },

  // Dashboard
  getDashboardData() {
    return api({
      url: '/dashboard',
      method: 'GET'
    });
  },

  // Requests for out of stock articles
  getOutOfStockArticles(payload) {
    return api({
      url: '/articles/get-articles-out-of-stock-requests',
      method: 'POST',
      data: payload,
    });
  },
  getOutOfStockArticle(id) {
    return api({
      url: `/articles/articles-out-of-stock-requests/${id}`,
      method: 'GET',
    });
  },
  updateOutOfStockArticle(payload) {
    return api({
      url: '/articles/update-article-out-of-stock-request',
      method: 'PUT',
      data: payload,
    });
  },
  deleteOutOfStockArticles(payload) {
    return api({
      url: '/articles/delete-article-out-of-stock-requests',
      method: 'DELETE',
      data: payload
    });
  },

  // Allowed Document Extensions
  getAllowedDocumentExtensions() {
    return api({
      url: '/allowed-document-extensions',
      method: 'GET',
    });
  },
};
